import React from 'react';
import { Box, Checkbox, FormControlLabel, Radio, Stack, TextField, Typography } from '@mui/material';
import { RequirementImage, RequirementOption } from './calculatorApi';
import { CalcImageData, ImageThumb } from './CalcImage';

const toImageData = (images: RequirementImage[]): CalcImageData[] =>
    (images || []).map((i) => ({
        articleNumber: i.articleNumber,
        name: i.name,
        titel: i.titel,
        beschreibung: i.beschreibung,
        haupt: i.haupt,
        crop: i.crop,
    }));

type OptMap = Record<string, number>;
type OptUpdater = (fn: (prev: OptMap) => OptMap) => void;

const OptionRow: React.FC<{
    option: RequirementOption;
    siblings: string[] | null;
    opt: OptMap;
    onChange: OptUpdater;
    depth: number;
}> = ({ option, siblings, opt, onChange, depth }) => {
    const menge = opt[option.requirementId] || 0;
    const selected = menge > 0;

    const setMenge = (val: number) =>
        onChange((prev) => {
            const next = { ...prev };
            if (val > 0) next[option.requirementId] = val;
            else delete next[option.requirementId];
            // Exklusivgruppe: das Aktivieren einer Option nullt die anderen derselben Gruppe.
            if (siblings && val > 0) {
                for (const s of siblings) if (s !== option.requirementId) delete next[s];
            }
            return next;
        });

    let control: React.ReactNode;
    if (option.inputKind === 'Spin') {
        control = (
            <TextField
                type="number"
                size="small"
                value={menge || ''}
                onChange={(e) => setMenge(Number(e.target.value) || 0)}
                slotProps={{ htmlInput: { min: 0, step: 1, style: { width: 64 } } }}
            />
        );
    } else if (siblings) {
        // Exklusive Option: erneuter Klick auf die gewählte hebt sie wieder auf.
        control = (
            <Radio
                size="small"
                checked={selected}
                onClick={() => setMenge(selected ? 0 : option.defaultAnzahl || 1)}
            />
        );
    } else {
        control = (
            <Checkbox
                size="small"
                checked={selected}
                onChange={(e) => setMenge(e.target.checked ? option.defaultAnzahl || 1 : 0)}
            />
        );
    }

    return (
        <Box sx={{ mt: 0.5 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                {control}
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2">{option.description || option.requirementId}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {option.requirementId}
                    </Typography>
                </Box>
                <ImageThumb title={option.description || option.requirementId} images={toImageData(option.images)} />
            </Stack>
            {option.options && option.options.length > 0 && selected && (
                <OptionsInput options={option.options} opt={opt} onChange={onChange} depth={depth + 1} />
            )}
        </Box>
    );
};

/** Rekursive Options-Eingabe einer Instanz: Exklusivgruppen (r_gruppe) zuerst, dann Einzeloptionen. */
export const OptionsInput: React.FC<{
    options: RequirementOption[];
    opt: OptMap;
    onChange: OptUpdater;
    depth?: number;
}> = ({ options, opt, onChange, depth = 0 }) => {
    const groups = new Map<number, RequirementOption[]>();
    const singles: RequirementOption[] = [];
    for (const o of options) {
        if (o.exclusiveGroup != null) {
            if (!groups.has(o.exclusiveGroup)) groups.set(o.exclusiveGroup, []);
            groups.get(o.exclusiveGroup)!.push(o);
        } else {
            singles.push(o);
        }
    }

    const rows: { option: RequirementOption; siblings: string[] | null }[] = [];
    Array.from(groups.values()).forEach((opts) => {
        const sib = opts.map((x) => x.requirementId);
        for (const o of opts) rows.push({ option: o, siblings: sib });
    });
    for (const o of singles) rows.push({ option: o, siblings: null });

    return (
        <Box
            sx={{
                mt: 0.5,
                pl: 1.5,
                ml: 1,
                borderLeft: (theme) => `2px solid ${theme.palette.divider}`,
            }}
        >
            {rows.map(({ option, siblings }) => (
                <OptionRow
                    key={option.requirementId}
                    option={option}
                    siblings={siblings}
                    opt={opt}
                    onChange={onChange}
                    depth={depth}
                />
            ))}
        </Box>
    );
};
